<template>
  <ElTooltip
    v-model:visible="visible"
    :content="content"
    :placement="placement"
    :disabled="disabled"
    :offset="offset"
    :show-arrow="showArrow"
    :popper-class="popperClass"
    :enterable="enterable"
    :hide-after="hideAfter"
    :auto-close="autoClose"
    :manual="manual"
    :popper-options="popperOptions"
    :show-after="showAfter"
    :transition="transition"
    :virtual-triggering="virtualTriggering"
    :virtual-ref="virtualRef"
    :trigger="trigger"
    :effect="effect"
    :raw-content="rawContent"
    :persistent="persistent"
    @show="handleShow"
    @hide="handleHide"
    @before-show="handleBeforeShow"
    @before-hide="handleBeforeHide"
  >
    <slot></slot>
    <template v-if="$slots.content" #content>
      <slot name="content"></slot>
    </template>
  </ElTooltip>
</template>

<script setup lang="ts">
/**
 * Tooltip 提示组件
 * 基于 Element Plus Tooltip 组件封装
 * @author AI Assistant
 * @date 2024-01-01
 */

import { ref, computed } from 'vue'
import type { Placement } from '@floating-ui/dom'

// 定义组件属性接口
interface Props {
  content?: string // 提示内容
  placement?: Placement // 提示位置
  disabled?: boolean // 是否禁用
  offset?: number // 偏移量
  showArrow?: boolean // 是否显示箭头
  popperClass?: string // 弹出层类名
  enterable?: boolean // 鼠标是否可进入提示框
  hideAfter?: number // 延迟隐藏时间
  autoClose?: number // 自动关闭时间
  manual?: boolean // 是否手动控制
  popperOptions?: any // Popper.js 选项
  showAfter?: number // 延迟显示时间
  transition?: string // 过渡动画
  virtualTriggering?: boolean // 是否虚拟触发
  virtualRef?: any // 虚拟引用
  trigger?: 'hover' | 'click' | 'focus' | 'contextmenu' // 触发方式
  effect?: 'dark' | 'light' // 主题效果
  rawContent?: boolean // 是否原始内容
  persistent?: boolean // 是否持久化
  modelValue?: boolean // v-model 绑定值
}

// 定义事件接口
interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'show'): void
  (e: 'hide'): void
  (e: 'before-show'): void
  (e: 'before-hide'): void
}

// 定义属性默认值
const props = withDefaults(defineProps<Props>(), {
  placement: 'top',
  disabled: false,
  offset: 12,
  showArrow: true,
  enterable: true,
  hideAfter: 200,
  autoClose: 0,
  manual: false,
  showAfter: 0,
  transition: 'el-fade-in-linear',
  virtualTriggering: false,
  trigger: 'hover',
  effect: 'dark',
  rawContent: false,
  persistent: false,
  modelValue: false
})

// 定义事件
const emit = defineEmits<Emits>()

// 响应式数据
const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

/**
 * 显示事件处理
 */
const handleShow = (): void => {
  emit('show')
}

/**
 * 隐藏事件处理
 */
const handleHide = (): void => {
  emit('hide')
}

/**
 * 显示前事件处理
 */
const handleBeforeShow = (): void => {
  emit('before-show')
}

/**
 * 隐藏前事件处理
 */
const handleBeforeHide = (): void => {
  emit('before-hide')
}
</script>

<style scoped>
.dh-tooltip {
  /* 自定义样式 */
}
</style>
